<script setup>
/* eslint-disable */
import { useGameStore } from '@/store/game'
import { ref, computed, watch, nextTick } from 'vue'
import AllChocobosView from './AllChocobosView.vue'
import AllJockeys from './AllJockeys.vue'
import TopPerformers from './TopPerformers.vue'
import RaceSetup from './RaceSetup.vue'
import RaceResults from './RaceResults.vue'
import ChocoboTraining from './ChocoboTraining.vue'
import { BreedingManager } from '@/services/breedingManager'
import ChocoboAuction from './ChocoboAuction.vue'
import RanchOverview from './RanchOverview.vue'
import ChocoboNegotiation from '@/components/RanchOverview.vue'
import ChocoboBreeding from './ChocoboBreeding.vue'
import ChocoboDetails from './ChocoboDetails.vue'
import ChocoboExpedition from './ChocoboExpedition.vue'
import { getStatGrade, getStatGradeClass } from '@/utils/chocoboStats'
import { TRAINING_TYPES } from '@/models/chocobos'
import { RACE_REWARDS } from '@/raceLogic'
import { getRegionDisplayName } from '@/constants/regionConstants'
import { NPCOwnerManager } from '@/services/npcOwnerManager'
import SaveManagement from './SaveManagement.vue'
import { GameActions, GameActionTypes } from '@/utils/gameActions'
import { GaiaCalendar } from '@/utils/gaiaCalendar'
import TransitRoutes from './TransitRoutes.vue'
import MonthlyResultsPopup from './MonthlyResultsPopup.vue'
import { MonthlyRaceManager } from '@/services/monthlyRaceManager'
import PlayerInventory from './PlayerInventory.vue'
import BreedingProgress from './BreedingProgress.vue'
import NotificationCenter from './NotificationCenter.vue'
import BidNotifications from './BidNotifications.vue'
import PerformanceNotifications from './PerformanceNotifications.vue'
import ItemUseModal from './ItemUseModal.vue'
import CharmEquipModal from './CharmEquipModal.vue'
import OfferModal from './OfferModal.vue'
import BidManagement from './BidManagement.vue'
import { getChocoboColorClass, getBehaviorClass, getTrainingClass } from '@/utils/colorUtils'
import DarkModeToggle from './DarkModeToggle.vue'
import { useDarkModeStore } from '@/store/darkMode'
import { applyThemeClasses } from '@/utils/themeColors'
import { debugMonthlyPrizes } from '@/utils/debugMonthlyPrizes'

const darkModeStore = useDarkModeStore()
const showInventory = ref(false)
const showItemUseModal = ref(false)

// Expose debug function for console access
if (typeof window !== 'undefined') {
  window.debugMonthlyPrizes = () => debugMonthlyPrizes(gameStore);
}
const showCharmEquipModal = ref(false)
const selectedItem = ref(null)

// Import BidManager for bid count
import { BidManager } from '@/services/bidManager'
const showSaveManager = ref(false)
const showRaceSetup = ref(false)
const showRaceResults = ref(false)
const currentRaceResults = ref(null)
const selectedChocoboForRace = ref(null)
const showAuction = ref(false)
const showTraining = ref(false)
const selectedChocoboForTraining = ref(null)
const showBreeding = ref(false)
const selectedChocoboForBreeding = ref(null)
const showDetails = ref(false)
const selectedChocoboForDetails = ref(null)
const showExpedition = ref(false)
const showMobileMenu = ref(false)
const selectedRanch = ref(null)
const selectedChocoboForTrade = ref(null)
const initialOffer = ref(null)
const showNegotiation = ref(false)
const showTransit = ref(false)
const showTransitRoutes = ref(false)
const selectedChocoboForTransit = ref(null)
const showMonthlyResults = ref(false)
const monthlyResults = ref(null)
const showOfferModal = ref(false)
const currentBid = ref(null)
const showingNotifications = ref(false)
const npcNotifications = ref([]);
const currentNotificationIndex = ref(0);


const props = defineProps({
  bid: {
    type: Object,
    required: false
  }
})

const openTransitRoutes = (chocobo) => {
  selectedChocoboForTransit.value = chocobo
  showTransitRoutes.value = true
}

const handleRejectOffer = () => {
  showOfferModal.value = false;
  showingNotifications.value = true;
  currentBid.value = null;
}

const handleCloseMonthlyResults = () => {
  showMonthlyResults.value = false
  // Clear the month end rewards after displaying
  if (gameStore.monthEndRewards) {
    gameStore.monthEndRewards = null
  }
}

const handleCloseRaceResults = () => {
  showRaceResults.value = false
  currentRaceResults.value = null
  
  // Force a save after closing race results to ensure any month-end processing is persisted
  gameStore.saveGame()
}

const handleAcceptOffer = async () => {
  if (!currentBid.value) return;
  
  const { owner, chocobo, amount } = currentBid.value;
  
  gameStore.playerOwner.money += amount;
  owner.money -= amount;

  const chocoboIndex = gameStore.playerOwner.chocoboIds.indexOf(chocobo.id);
  if (chocoboIndex !== -1) {
    gameStore.playerOwner.chocoboIds.splice(chocoboIndex, 1);
    owner.chocoboIds.push(chocobo.id);
  }

  gameStore.addTransaction({
    seller: gameStore.playerOwner,
    buyer: owner,
    chocobo,
    price: amount,
    date: gameStore.currentDay
  });

  await GameActions.performAction(GameActionTypes.PURCHASE);
  
  showOfferModal.value = false;
  currentBid.value = null;
  showingNotifications.value = true;
  
  // Move to next notification if available
  if (currentNotificationIndex.value < npcNotifications.value.length - 1) {
    currentNotificationIndex.value++;
  } else {
    // Close notifications if we're at the end
    showingNotifications.value = false;
    currentView.value = 'ranch';
  }
};

const handleViewOffer = (bid) => {
  if (!bid?.owner || !bid?.chocobo) return;
  
  currentBid.value = {
    amount: bid.amount,
    owner: bid.owner,
    chocobo: bid.chocobo
  };
  
  if (currentBid.value?.owner && currentBid.value?.chocobo) {
    showOfferModal.value = true;
  }
  
  showRaceResults.value = false;
};


const formattedDate = computed(() => {
  return GaiaCalendar.formatGameDate(gameStore.currentDay)
})

const handleTransitComplete = (earnings) => {
  gameStore.playerOwner.money += earnings
  gameStore.saveGame()
}

const closeNegotiation = () => {
  showNegotiation.value = false
  selectedRanch.value = null
  selectedChocoboForTrade.value = null
  initialOffer.value = null
  if (currentRaceResults.value) {
    showRaceResults.value = true
  }
}

const gameStore = useGameStore()
const currentView = ref('ranch')

const openDetails = (chocobo) => {
  selectedChocoboForDetails.value = chocobo
  showDetails.value = true
}


const openBreeding = (chocobo) => {
  selectedChocoboForBreeding.value = chocobo
  showBreeding.value = true
}

watch(
  () => gameStore.currentDay,
  (newDay, oldDay) => {
    if (MonthlyRaceManager.isMonthEnd(oldDay, newDay)) {
      // Use nextTick to ensure store processing completes first
      nextTick(() => {
        const prevMonth = MonthlyRaceManager.getMonthFromDay(oldDay)
        const year = Math.floor(oldDay / (32 * 12)) + 7
        const results = MonthlyRaceManager.getMonthlyBestTimes(gameStore.chocobos, prevMonth)
        
        const awards = gameStore.monthEndRewards || []
        results.awards = awards
        
        monthlyResults.value = {
          bestTimes: results,
          prevMonth,
          year
        }
        showMonthlyResults.value = true
        
        // Don't clear monthEndRewards here - let it persist
        // gameStore.monthEndRewards = null
      })
    }
  }
)

const handleRaceComplete = async (raceData) => {
  try {
    // Ensure we have valid results data
    if (!raceData?.results) {
      console.error('Invalid race data received');
      return;
    }

    // Create a new array of processed results
    const processedResults = raceData.results.map(result => {
      // Create new object for each result to avoid reactivity issues
      const processedResult = { ...result };
      
      // Only process player chocobo results
      if (gameStore.playerOwner.chocoboIds.includes(result.chocobo.id)) {
        let moneyEarned = 0;
        if (result.position <= 3) {
          moneyEarned = Math.floor(
            RACE_REWARDS[raceData.raceLength][result.position].money * 
            raceData.circuit.rewardMultiplier
          );
          gameStore.playerOwner.money += moneyEarned;
        }

        const baseXP = RACE_REWARDS[raceData.raceLength][result.position]?.xp || 
                      RACE_REWARDS[raceData.raceLength].participation.xp;
        const totalXP = Math.floor(baseXP * raceData.circuit.xpMultiplier);
        
        processedResult.experienceGained = totalXP;
        processedResult.moneyEarned = moneyEarned;
        processedResult.levelUp = result.chocobo.gainExperience(totalXP);
      }
      
      return processedResult;
    });

    // Get NPC notifications after race
    NPCOwnerManager.processTrainingDecisions(gameStore);
    npcNotifications.value = NPCOwnerManager.processAuctionAndSales(gameStore);
    currentNotificationIndex.value = 0;

    // Set current race results
    currentRaceResults.value = {
      results: processedResults,
      withVisualization: raceData.withVisualization,
      raceType: raceData.raceLength,
      circuit: raceData.circuit
    };

    // Process game day advancement
    await GameActions.performAction(GameActionTypes.RACE);
    
    // Update UI state
    showRaceSetup.value = false;
    showRaceResults.value = true;

  } catch (error) {
    console.error('Error completing race:', error);
  }
};



const handleViewNewAuctions = (auctions) => {
  showRaceResults.value = false
  showAuction.value = true
}

const handleUseItem = (item) => {
  selectedItem.value = item
  showItemUseModal.value = true
}

const handleEquipItem = (item) => {
  selectedItem.value = item
  showCharmEquipModal.value = true
}

const handleItemUsed = ({ item, chocobo }) => {
  showItemUseModal.value = false
  selectedItem.value = null
  
  // Show success notification
  const message = `${item.name} has been used on ${chocobo.name}!`
  // You can add a toast notification here if you have a notification system
}

const handleCharmEquipped = ({ item, chocobo }) => {
  showCharmEquipModal.value = false
  selectedItem.value = null
  
  // Show success notification
  const message = `${item.name} has been equipped on ${chocobo.name}!`
  // You can add a toast notification here if you have a notification system
}


const openRaceSetup = (chocobo) => {
  selectedChocoboForRace.value = chocobo
  showRaceSetup.value = true
  showRaceResults.value = false
}


const openAuction = () => {
  showAuction.value = true
}

const closeAuction = () => {
  showAuction.value = false
}

const navigateTo = (view) => {
  currentView.value = view;
  if (view === 'jockeys') {
    showAuction.value = false;
    showRaceSetup.value = false;
    showRaceResults.value = false;
    showTraining.value = false;
    showBreeding.value = false;
    showDetails.value = false;
    showExpedition.value = false;
    showNegotiation.value = false;
    showTransit.value = false;
    showTransitRoutes.value = false;
  }
}

const getGenderIcon = (gender) => {
  return gender === 'male' ? '♂' : '♀'
}

const getGenderClass = (gender) => {
  return gender === 'male' ? 'text-blue-500' : 'text-pink-500'
}

const openTraining = (chocobo) => {
  selectedChocoboForTraining.value = chocobo
  showTraining.value = true
}

// Bid count for navigation badge
const activeBidsCount = computed(() => {
  return BidManager.getActiveBids(gameStore).length
})

const handleTrainingComplete = (improvements) => {
  // Training completed
}

const getTrainingIcon = (trainingType) => {
  switch(trainingType) {
    case TRAINING_TYPES.SPEED:
      return '🏃'
    case TRAINING_TYPES.STAMINA:
      return '💪'
    case TRAINING_TYPES.BEHAVIOR:
      return '🎯'
    default:
      return '🏃' // Default to speed icon
  }
}

const getTrainingLabel = (trainingType) => {
  switch(trainingType) {
    case TRAINING_TYPES.SPEED:
      return 'Speed Focus'
    case TRAINING_TYPES.STAMINA:
      return 'Stamina Focus'
    case TRAINING_TYPES.BEHAVIOR:
      return 'Behavior Focus'
    default:
      return 'Speed Focus'
  }
}

const updateTrainingFocus = (chocobo, type) => {
  chocobo.setTrainingFocus(type)
}

const viewChocoboFromNotification = (chocoboId) => {
  const chocobo = gameStore.chocobos.find(c => c.id === chocoboId)
  if (chocobo) {
    openDetails(chocobo)
  }
}

const playerChocobos = computed(() => gameStore.playerChocobos)

const inventoryItemCount = computed(() => {
  if (!gameStore.playerOwner.inventory) return 0
  // Count consumable items that haven't been used
  return gameStore.playerOwner.inventory.filter(item => 
    item.type === 'consumable' && !item.isUsed
  ).length
})

</script>

<template>
  <div :class="applyThemeClasses('layout.page')">
    <nav :class="`sticky top-0 z-40 ${applyThemeClasses('layout.card')} shadow-sm`">
      <div :class="applyThemeClasses('layout.divider')">
        <!-- Mobile Nav -->
        <div class="flex flex-col py-2 sm:hidden">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center">
              <button 
                @click="() => navigateTo('ranch')"
                :class="[
                  'text-left truncate max-w-[200px]',
                  currentView === 'ranch' 
                    ? 'text-gray-900 font-medium' 
                    : 'text-gray-500'
                ]"
              >
                <span :class="applyThemeClasses('text.primary')">{{ gameStore.playerOwner.ranchName }} 🏠</span>
                <div class="text-xs text-gray-600">
                  <span :class="applyThemeClasses('text.secondary')">{{ gameStore.playerOwner.chocoboIds.length }}/{{ gameStore.MAX_PLAYER_CHOCOBOS }} Stable Capacity</span>
                </div>
              </button>
            </div>
            
            <div class="flex items-center gap-2">
              <span :class="applyThemeClasses('text.money')">
                {{ gameStore.playerOwner.money.toLocaleString() }}G
              </span>
              <button 
                @click="showMobileMenu = !showMobileMenu"
                :class="applyThemeClasses('form.button.outline')"
              >
                <span class="sr-only">Open menu</span>
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Mobile Menu -->
          <div v-if="showMobileMenu" :class="`${applyThemeClasses('layout.card')} rounded-lg shadow-lg border mt-1`">
            <button
              @click="showExpedition = true; showMobileMenu = false"
              :class="applyThemeClasses('form.button.mobile')"
            >
              Go on Expedition
            </button>
            <button
              @click="showInventory = true; showMobileMenu = false"
              :class="applyThemeClasses('form.button.mobile')"
            >
              Inventory
              <span v-if="inventoryItemCount > 0" class="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                {{ inventoryItemCount }}
              </span>
            </button>
            <button
              @click="() => { openAuction(); showMobileMenu = false }"
              :class="applyThemeClasses('form.button.mobile')"
            >
              Auction House
            </button>
            <button
              @click="() => { navigateTo('ranches'); showMobileMenu = false }"
              :class="applyThemeClasses('form.button.mobile')"
            >
              Ranch Directory
            </button>
            <button
              @click="() => { navigateTo('all-chocobos'); showMobileMenu = false }"
              :class="applyThemeClasses('form.button.mobile')"
            >
              All Chocobos
            </button>
            <button 
              @click="() => navigateTo('jockeys')"
              :class="applyThemeClasses('form.button.mobile')"
            >
              Jockeys
            </button>
            <button
              @click="() => { navigateTo('top-performers'); showMobileMenu = false }"
              :class="applyThemeClasses('form.button.mobile')"
            >
              Top Performers
            </button>
            <button
              @click="() => { navigateTo('bids'); showMobileMenu = false }"
              :class="applyThemeClasses('form.button.mobile')"
            >
              Bids
              <span v-if="activeBidsCount > 0" class="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                {{ activeBidsCount }}
              </span>
            </button>
            <button
              @click="darkModeStore.toggle()"
              :class="applyThemeClasses('form.button.mobile')"
            >
              {{ darkModeStore.isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode' }}
            </button>
            <button
              @click="showSaveManager = true; showMobileMenu = false"
              :class="applyThemeClasses('form.button.mobile')"
            >
              Save Game
            </button>
          </div>
        </div>

        <!-- Desktop Nav -->
        <div class="hidden sm:flex sm:justify-between sm:h-16 sm:items-center">
          <div class="flex items-center">
            <button 
              @click="() => navigateTo('ranch')"
              :class="[
                'inline-flex items-center px-4 py-2 border-b-2',
                currentView === 'ranch' 
                  ? 'border-yellow-500 text-gray-900' 
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              ]"
            >
              <div>
                <span :class="applyThemeClasses('text.primary')">{{ gameStore.playerOwner.ranchName }} 🏠</span>
                <div class="text-sm text-gray-600">
                  <span :class="applyThemeClasses('text.secondary')">{{ gameStore.playerOwner.chocoboIds.length }}/{{ gameStore.MAX_PLAYER_CHOCOBOS }} Stable Capacity</span>
                </div>
              </div>
            </button>
          </div>
          
          <div class="flex items-center gap-2 sm:gap-4">
            <span :class="applyThemeClasses('text.money')">
              {{ gameStore.playerOwner.money.toLocaleString() }}G
            </span>
            <button 
              @click="showExpedition = true"
              class="px-3 py-1.5 bg-purple-500 text-white rounded text-sm hover:bg-purple-600"
            >
              Expedition
            </button>
            <button 
              @click="showInventory = true"
              class="px-3 py-1.5 bg-amber-500 text-white rounded text-sm hover:bg-amber-600 relative"
            >
              Inventory
              <span 
                v-if="inventoryItemCount > 0" 
                class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center"
              >
                {{ inventoryItemCount }}
              </span>
            </button>
            <button 
              @click="openAuction"
              class="px-3 py-1.5 bg-green-500 text-white rounded text-sm hover:bg-green-600"
            >
              Auction
            </button>
            <button 
              @click="() => navigateTo('ranches')"
              :class="[
                'px-3 py-1.5 text-sm rounded',
                currentView === 'ranches' 
                  ? 'bg-gray-100 text-gray-900' 
                  : applyThemeClasses('text.secondary')
              ]"
            >
              Ranches
            </button>
            <button 
              @click="() => navigateTo('all-chocobos')"
              :class="[
                'px-3 py-1.5 text-sm rounded',
                currentView === 'all-chocobos' 
                  ? 'bg-gray-100 text-gray-900' 
                  : applyThemeClasses('text.secondary')
              ]"
            >
              All Chocobos
            </button>
            <button 
              @click="() => navigateTo('jockeys')"
              :class="[
                'px-3 py-1.5 text-sm rounded',
                currentView === 'jockeys' 
                  ? 'bg-gray-100 text-gray-900' 
                  : applyThemeClasses('text.secondary')
              ]"
            >
              Jockeys
            </button>
            <button 
              @click="() => navigateTo('bids')"
              :class="[
                'px-3 py-1.5 text-sm rounded relative',
                currentView === 'bids' 
                  ? 'bg-gray-100 text-gray-900' 
                  : applyThemeClasses('text.secondary')
              ]"
            >
              Bids
              <span v-if="activeBidsCount > 0" class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {{ activeBidsCount }}
              </span>
            </button>
            
            <button 
              @click="() => navigateTo('top-performers')"
              :class="[
                'px-3 py-1.5 text-sm rounded',
                currentView === 'top-performers' 
                  ? 'bg-gray-100 text-gray-900' 
                  : applyThemeClasses('text.secondary')
              ]"
            >
              Top Performers
            </button>
            <DarkModeToggle />
            <button 
              @click="showSaveManager = true"
              class="px-3 py-1.5 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
            >
              Save Game
            </button>
          </div>
        </div>
      </div>
    </nav>
    <!-- Main Content Area -->
    <div class="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <!-- Ranch View -->
      <div v-if="currentView === 'ranch'" class="px-4 py-6 sm:px-0">
        <div :class="`rounded-lg shadow-lg ${applyThemeClasses('layout.card')}`">
          <h1 :class="applyThemeClasses('text.primary')">{{ gameStore.playerOwner.ranchName }} | Stable Capacity: {{ gameStore.playerOwner.chocoboIds.length }}/{{ gameStore.MAX_PLAYER_CHOCOBOS }}
          </h1>
          <div :class="`rounded-lg shadow-lg ${applyThemeClasses('layout.card')}`">
              <div class="space-y-2">
                <div class="flex space-x-4 text-gray-600">
                  <p :class="applyThemeClasses('text.secondary')">Owner: {{ gameStore.playerOwner.name }}</p>
                  <p :class="applyThemeClasses('text.secondary')">Region: {{ getRegionDisplayName(gameStore.playerOwner.homeRegion) }}</p>
                </div>
                <div class="text-amber-800 font-medium">
                  <p :class="applyThemeClasses('text.money')">Money: {{ gameStore.playerOwner.money.toLocaleString() }} G</p>
                  <p :class="applyThemeClasses('text.secondary')">Date: {{ formattedDate }}</p>
                </div>
            </div>
          </div>
        </div>

        <!-- Active Breedings -->
        <div :class="`rounded-lg shadow-lg ${applyThemeClasses('layout.card')}`">
          <BreedingProgress />
        </div>

        <!-- Player's Chocobos -->
        <div :class="`rounded-lg shadow-lg ${applyThemeClasses('layout.card')}`">
          <h2 :class="applyThemeClasses('text.primary')">Your Chocobos</h2>
          
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div 
              v-for="chocobo in playerChocobos" 
              :key="chocobo.id"
              :class="`rounded-lg shadow-lg ${applyThemeClasses('layout.card')}`"
            >
              <!-- Chocobo Header -->
              <div :class="[getChocoboColorClass(chocobo.color), 'relative']">
                <img 
                  :src="chocobo.imageUrl" 
                  :alt="chocobo.name"
                  class="w-full h-48 object-cover"
                />
                <div class="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                  <div class="flex justify-between items-start">
                    <div>
                      <h3 class="font-bold text-lg text-white">{{ chocobo.name }}</h3>
                      <p class="text-sm text-white opacity-90">
                        {{ chocobo.color.charAt(0).toUpperCase() + chocobo.color.slice(1) }} Chocobo
                      </p>
                    </div>
                    <span 
                      :class="[getGenderClass(chocobo.gender), 'text-2xl font-bold']"
                    >
                      {{ getGenderIcon(chocobo.gender) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Chocobo Stats -->
              <div class="p-4 space-y-2">
                <div class="grid grid-cols-2 gap-2">
                  <div 
                      :class="[
                        getStatGradeClass(getStatGrade(chocobo.speed)),
                        'px-2 py-1 rounded text-sm font-medium'
                      ]"
                    >
                      Speed: {{ getStatGrade(chocobo.speed) }}
                    </div>
                    <div 
                      :class="[
                        getStatGradeClass(getStatGrade(chocobo.stamina)),
                        'px-2 py-1 rounded text-sm font-medium'
                      ]"
                    >
                      Stamina: {{ getStatGrade(chocobo.stamina) }}
                    </div>
                  <div class="text-sm">
                    <span :class="applyThemeClasses('text.secondaryBold')">Level: </span> 
                    <span :class="applyThemeClasses('text.secondary')">{{ chocobo.level }}</span>
                  </div>
                  <div class="text-sm">
                    <span :class="applyThemeClasses('text.secondaryBold')">XP: </span> 
                    <span :class="applyThemeClasses('text.secondary')" v-if="chocobo.level < chocobo.maxLevel">{{ chocobo.getXpToNextLevel() }} to next level</span>
                    <span :class="applyThemeClasses('text.secondary')" v-else>Max Level</span>
                  </div>
                  <!-- Breeding Status -->
                  <div v-if="chocobo.isBreeding" class="mt-2">
                    <div :class="`px-3 py-2 rounded-lg text-xs font-medium border-l-4 ${BreedingManager.getStageDisplayInfo(chocobo.getBreedingStage(gameStore.currentDay)).color}`">
                      <div class="flex items-center justify-between">
                        <div class="flex items-center space-x-2">
                          <span class="text-base">{{ BreedingManager.getStageDisplayInfo(chocobo.getBreedingStage(gameStore.currentDay)).icon }}</span>
                          <span class="font-semibold">{{ BreedingManager.getStageDisplayInfo(chocobo.getBreedingStage(gameStore.currentDay)).text }}</span>
                        </div>
                        <div class="text-right">
                          <div class="font-bold">{{ chocobo.getBreedingDaysRemaining(gameStore.currentDay) }}</div>
                          <div class="text-xs opacity-75">days left</div>
                        </div>
                      </div>
                      <div class="mt-2 w-full bg-black/10 rounded-full h-1">
                        <div 
                          class="bg-white/40 h-1 rounded-full transition-all duration-300"
                          :style="{ width: `${((14 - chocobo.getBreedingDaysRemaining(gameStore.currentDay)) / 14) * 100}%` }"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Add Training Focus indicator after the stats -->
                <div class="p-4 space-y-2">
                  <div class="grid grid-cols-2 gap-2">
                    <!-- ... existing stats ... -->
                  </div>

                  <!-- Training Focus Indicator -->
                  <div class="flex items-center justify-between">
                    <div 
                      :class="[
                        getTrainingClass(chocobo.trainingType),
                        'px-2 py-1 rounded-full text-sm flex items-center gap-2'
                      ]"
                    >
                      <span>{{ getTrainingIcon(chocobo.trainingType) }}</span>
                      <span>{{ getTrainingLabel(chocobo.trainingType) }}</span>
                    </div>
                    
                    <!-- Training Focus Dropdown -->
                    <select 
                      :value="chocobo.trainingType"
                      @change="(e) => updateTrainingFocus(chocobo, e.target.value)"
                      :disabled="!BreedingManager.isChocoboAvailable(chocobo)"
                      :class="`rounded-lg ${applyThemeClasses('form.select')} ${!BreedingManager.isChocoboAvailable(chocobo) ? 'opacity-50 cursor-not-allowed' : ''}`"
                      :title="!BreedingManager.isChocoboAvailable(chocobo) ? 'Chocobo is currently breeding' : ''"
                    >
                      <option :value="TRAINING_TYPES.SPEED">Speed Training</option>
                      <option :value="TRAINING_TYPES.STAMINA">Stamina Training</option>
                      <option :value="TRAINING_TYPES.BEHAVIOR">Behavior Training</option>
                    </select>
                  </div>

                  <!-- Behavior Training Progress (only show if behavior training selected) -->
                  <div 
                    v-if="chocobo.trainingType === TRAINING_TYPES.BEHAVIOR"
                    class="text-sm text-gray-600"
                  >
                    Behavior Progress: {{ chocobo.consecutiveBehaviorTraining }}/6 levels
                  </div>
                </div>
                <!-- Behavior Badge -->
                <div 
                  v-if="chocobo.behavior"
                  :class="[getBehaviorClass(chocobo.behaviorId), 'inline-block px-2 py-1 rounded-full text-sm mt-2']"
                >
                  {{ chocobo.behavior.description }}
                </div>

                <!-- X-Factor Indicators -->
                <div class="flex space-x-2 mt-2">
                  <span 
                    v-if="chocobo.hasSpeedXFactor" 
                    class="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-xs"
                  >
                    Speed X-Factor
                  </span>
                  <span 
                    v-if="chocobo.hasStaminaXFactor" 
                    class="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-xs"
                  >
                    Stamina X-Factor
                  </span>
                </div>
              </div>

              <!-- Action Buttons -->
              <div :class="`rounded-lg shadow-lg ${applyThemeClasses('layout.card')}`">
                <div class="flex-wrap space-x-2">
                  <button 
                    @click="openRaceSetup(chocobo)"
                    :disabled="!BreedingManager.isChocoboAvailable(chocobo)"
                    :class="`rounded-lg text-sm ${applyThemeClasses('form.button.race')} ${!BreedingManager.isChocoboAvailable(chocobo) ? 'opacity-50 cursor-not-allowed' : ''}`"
                    :title="!BreedingManager.isChocoboAvailable(chocobo) ? 'Chocobo is currently breeding' : ''"
                  >
                    Race
                  </button>
                  <button 
                    @click="() => { selectedChocoboForTransit = chocobo; showTransitRoutes = true; }"
                    :disabled="!BreedingManager.isChocoboAvailable(chocobo)"
                    :class="`rounded-lg text-sm ${applyThemeClasses('form.button.transit')} ${!BreedingManager.isChocoboAvailable(chocobo) ? 'opacity-50 cursor-not-allowed' : ''}`"
                    :title="!BreedingManager.isChocoboAvailable(chocobo) ? 'Chocobo is currently breeding' : ''"
                  >
                    Transit
                  </button>
                  <button 
                    @click="openDetails(chocobo)"
                    :class="`rounded-lg ${applyThemeClasses('form.button.outline')}`"
                  >
                    Details
                  </button>
                  <button 
                    @click="openBreeding(chocobo)"
                    :disabled="chocobo.level < 5"
                    :class="[
                      'px-2 py-1 text-white rounded text-sm',
                      chocobo.level >= 5 
                        ? `rounded-lg text-sm ${applyThemeClasses('form.button.breed')}`
                        : 'bg-gray-400 cursor-not-allowed'
                    ]"
                  >
                    {{ chocobo.level >= 5 ? 'Breed' : `Can't Breed Until Lv5` }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="playerChocobos.length === 0" class="text-center py-12 text-gray-500">
            No Chocobos owned yet
          </div>
        </div>
      </div>

      <ChocoboDetails
        v-if="showDetails"
        :chocobo="selectedChocoboForDetails"
        @close="showDetails = false"
      />
      <RanchOverview 
        v-if="currentView === 'ranches'" 
        @close="currentView = 'ranch'"
      />
      <!-- Negotiation Dialog -->
      <RanchOverview
        v-if="showNegotiation"
        :selected-ranch="selectedRanch"
        :selected-chocobo="selectedChocoboForTrade"
        :initial-offer="initialOffer"
        @close="closeNegotiation"
      />
      <AllChocobosView v-if="currentView === 'all-chocobos'" />
      <AllJockeys v-if="currentView === 'jockeys'" />
      <BidManagement 
        v-if="currentView === 'bids'" 
        @view-chocobo="viewChocoboDetails"
      />
      <TopPerformers v-if="currentView === 'top-performers'" />
      <ChocoboTraining
      v-if="showTraining"
      :chocobo="selectedChocoboForTraining"
      @training-complete="handleTrainingComplete"
      @close="showTraining = false"
      />
      <RaceSetup
        v-if="showRaceSetup"
        :selected-chocobo="selectedChocoboForRace"
        @close="showRaceSetup = false"
        @race-complete="handleRaceComplete"
      />
      <RaceResults
        v-if="showRaceResults"
        :results="currentRaceResults"
        @close="handleCloseRaceResults"
        @view-offer="handleViewOffer"
        @view-auctions="handleViewNewAuctions"
      />
      <OfferModal
        v-if="showOfferModal && currentBid"
        :bid="currentBid"
        @accept="handleAcceptOffer"
        @reject="handleRejectOffer"
      />
      <!-- Add Auction Modal -->
      <ChocoboAuction
        v-if="showAuction"
        @close="closeAuction"
      />
      <ChocoboBreeding
        v-if="showBreeding"
        :selected-chocobo="selectedChocoboForBreeding"
        @close="showBreeding = false"
      />
      <ChocoboExpedition
        v-if="showExpedition"
        @close="showExpedition = false"
      />
      </div>
      <SaveManagement
        v-if="showSaveManager"
        @close="showSaveManager = false"
      />
      <TransitRoutes
        v-if="showTransitRoutes && selectedChocoboForTransit"
        :chocobo="selectedChocoboForTransit"
        @close="showTransitRoutes = false; selectedChocoboForTransit = null;"
      />
      <MonthlyResultsPopup
        v-if="showMonthlyResults"
        :best-times="monthlyResults.bestTimes"
        :prev-month="monthlyResults.prevMonth"
        :year="monthlyResults.year"
        @close="handleCloseMonthlyResults"
      />
      <PlayerInventory
        v-if="showInventory"
        @close="showInventory = false"
        @use-item="handleUseItem"
        @equip-item="handleEquipItem"
      />
      
      <!-- Item Use Modal -->
      <ItemUseModal
        v-if="showItemUseModal && selectedItem"
        :item="selectedItem"
        @close="showItemUseModal = false; selectedItem = null"
        @item-used="handleItemUsed"
      />
      
      <!-- Charm Equip Modal -->
      <CharmEquipModal
        v-if="showCharmEquipModal && selectedItem"
        :item="selectedItem"
        @close="showCharmEquipModal = false; selectedItem = null"
        @charm-equipped="handleCharmEquipped"
      />
      
      <!-- Notification Center -->
      <NotificationCenter @view-chocobo="viewChocoboFromNotification" />
      
      <!-- Bid Notifications -->
      <BidNotifications 
        @view-bid="handleViewOffer"
        @view-all-bids="currentView = 'bids'"
        @view-chocobo="(chocoboId) => {
          const chocobo = gameStore.getChocoboById(chocoboId)
          if (chocobo) {
            selectedChocoboForDetails = chocobo
            showDetails = true
          }
        }"
      />
      
      <!-- Performance Notifications -->
      <PerformanceNotifications
        @view-chocobo="(chocoboId) => {
          const chocobo = gameStore.getChocoboById(chocoboId)
          if (chocobo) {
            selectedChocoboForDetails = chocobo
            showDetails = true
          }
        }"
      />
  </div>
</template>